import React from "react";
import {
  Group,
  Paper,
  Text,
  Button,
  Stack,
  ScrollArea,
  Tooltip,
} from "@mantine/core";
import { CSSProperties } from "react";
import { IconCheck } from "@tabler/icons-react";
import { AVAILABLE_TEMPLATES } from "../utils/FeatureLocks";

// Color constants
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_TEXT_BLACK = "#000";
const COLOR_BORDER_SELECTED = "#1976d2";
const COLOR_GRADIENT_START_VIBRANT = "#ff6b6b";
const COLOR_GRADIENT_END_VIBRANT = "#ffa500";
const COLOR_TEXT_WHITE = "#fff";
const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_WARM = "#2d5016";
const COLOR_BACKGROUND_ORGANIC = "#f5f5dc";

interface TemplateStyle {
  backgroundColor?: string;
  background?: string;
  backgroundImage?: string;
  backgroundSize?: string;
  color: string;
  padding: string;
  borderRadius: string;
  border: string;
  position: string;
  textAlign?: string;
}

interface Template {
  id: string;
  name: string;
  selected: boolean;
  available: boolean;
  content: string;
  description: string;
  style: TemplateStyle;
}

interface TemplateOptionsProps {
  templateSelected: string;
  onTemplateSelect: (templateId: string) => void;
}

const TemplateOptions: React.FC<TemplateOptionsProps> = ({
  templateSelected,
  onTemplateSelect,
}) => {
  // Only show the default template (generic)
  const templates: Template[] = [
    {
      id: "generic",
      name: "Default",
      selected:
        templateSelected === "generic" ||
        templateSelected === "website" ||
        !templateSelected,
      available: true,
      content: "Default Template",
      description: "Standard website template",
      style: {
        backgroundColor: COLOR_BACKGROUND_LIGHT,
        color: COLOR_TEXT_BLACK,
        padding: "40px",
        borderRadius: "8px",
        border: `2px solid ${COLOR_BORDER_SELECTED}`,
        position: "relative",
      },
    },
  ];

  return (
    <Stack gap="lg">
      <Text fw={600} size="lg">
        Choose a Template
      </Text>

      <ScrollArea h={350} scrollbarSize={6}>
        <Group gap="xl" wrap="wrap" style={{ padding: "15px" }}>
          {templates.map((template) => (
            <Stack key={template.id} align="center" gap="md">
              <Tooltip
                label="In development, coming soon!"
                disabled={template.available}
                position="top"
                withArrow
              >
                <Paper
                  onClick={() =>
                    template.available && onTemplateSelect(template.id)
                  }
                  style={
                    {
                      ...template.style,
                      width: "400px",
                      height: "300px",
                      cursor: template.available ? "pointer" : "not-allowed",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "40px",
                      opacity: template.available ? 1 : 0.5,
                      filter: template.available ? "none" : "grayscale(0.3)",
                    } as CSSProperties
                  }
                >
                  {template.selected && template.available && (
                    <IconCheck
                      size={28}
                      color={COLOR_BORDER_SELECTED}
                      style={{
                        position: "absolute",
                        top: "15px",
                        left: "15px",
                      }}
                    />
                  )}
                  <Text
                    fw={700}
                    size="xl"
                    style={{
                      textAlign: "center",
                      color: template.style.color,
                      marginBottom: "15px",
                      lineHeight: 1.2,
                    }}
                  >
                    {template.content}
                  </Text>
                  <Text
                    size="md"
                    style={{
                      textAlign: "center",
                      color: template.style.color,
                      opacity: 0.9,
                      lineHeight: 1.3,
                    }}
                  >
                    {template.description}
                  </Text>
                </Paper>
              </Tooltip>
              <Text
                fw={500}
                size="lg"
                c={template.available ? "dimmed" : "gray"}
              >
                {template.name}
              </Text>
            </Stack>
          ))}
        </Group>
      </ScrollArea>
    </Stack>
  );
};

export default TemplateOptions;
